import { useState, useEffect } from "react";
import axios from "axios";
import { Download, Tag, Wrench, Sparkles, ArrowUp, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

const API = '/api';

const TYPE_CONFIG = {
  feature: { label: "Nueva", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", icon: Sparkles },
  fix: { label: "Fix", color: "bg-red-500/20 text-red-400 border-red-500/30", icon: Wrench },
  improvement: { label: "Mejora", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: ArrowUp },
};

export default function Changelog() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedVersion, setExpandedVersion] = useState(null);

  useEffect(() => {
    axios.get(`${API}/extension/changelog`).then(res => {
      setData(res.data);
      if (res.data.changelog.length > 0) {
        setExpandedVersion(res.data.changelog[0].version);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div data-testid="changelog-page" className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-white tracking-tight">Historial de Versiones</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Version actual: <span className="text-cyan-400 font-mono">{data?.current_version}</span>
          </p>
        </div>
        <a
          href={`${API}/extension/download`}
          data-testid="download-extension-btn"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Descargar v{data?.current_version}
        </a>
      </div>

      <div className="space-y-3">
        {data?.changelog.map((release, idx) => {
          const isExpanded = expandedVersion === release.version;
          const isCurrent = release.version === data.current_version;

          return (
            <div
              key={release.version}
              data-testid={`release-${release.version}`}
              className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                isCurrent
                  ? 'border-blue-500/40 bg-blue-950/20'
                  : 'border-zinc-800 bg-zinc-900/50'
              }`}
            >
              <button
                onClick={() => setExpandedVersion(isExpanded ? null : release.version)}
                className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-zinc-800/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono font-bold ${
                    isCurrent
                      ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                      : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                  }`}>
                    <Tag className="w-3 h-3" />
                    v{release.version}
                  </div>
                  <span className="text-sm font-medium text-white">{release.title}</span>
                  {isCurrent && (
                    <span className="text-[9px] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded px-1.5 py-0.5 font-mono">
                      ACTUAL
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-zinc-600 font-mono">{release.date}</span>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-zinc-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-500" />
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="px-5 pb-4 border-t border-zinc-800/50">
                  <div className="space-y-2 mt-3">
                    {release.changes.map((change, cIdx) => {
                      const cfg = TYPE_CONFIG[change.type] || TYPE_CONFIG.improvement;
                      const Icon = cfg.icon;
                      return (
                        <div key={cIdx} className="flex items-start gap-2.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-bold border whitespace-nowrap mt-0.5 ${cfg.color}`}>
                            <Icon className="w-3 h-3" />
                            {cfg.label}
                          </span>
                          <span className="text-xs text-zinc-300 leading-relaxed">{change.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
